/**
 * 운영 프로세스 — 업무절차(워크플로) 디자인 뷰.
 *
 * Downloads/업무절차.html 디자인을 React 로 옮긴 컴포넌트:
 *   [상단] 눈썹 라벨 + 제목 + 진행률 큰 숫자 / 게이지 바
 *   [중단] 현재 단계(다크) · 다음 단계(화이트) 카드 2개
 *   [하단] 원형 노드 타임라인 — 단계 카드(펼침: 담당자/내용/체크리스트/
 *          관련 문서/하위 단계/액션) + "단계 추가"
 *
 * 데이터 소스는 props(fetcher/saver) 주입 — 운영 템플릿 페이지에서 사용.
 * 모든 변경은 낙관적 반영 후 즉시 저장(coalesce). 실패 시 알림 + 재로드.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';

import {
  OperationProcessData,
  OperationProcessRow,
} from '../../services/OperationProcessService';
import { OperationProcessFileService } from '../../services/OperationProcessFileService';
import WorkflowStepCard, { WorkflowActions, useWorkflowTokens } from './WorkflowStepCard';
import {
  addChecklistItem,
  addStep,
  addSubStep,
  buildWorkflowView,
  moveStep,
  moveSubStep,
  patchChild,
  patchRow,
  removeChecklistItem,
  removeStep,
  removeSubStep,
  setUnitDone,
  toggleChecklistItem,
} from './operationWorkflowModel';

const GOTHIC_A1_CSS =
  'https://fonts.googleapis.com/css2?family=Gothic+A1:wght@400;500;700;900&display=swap';

export interface OperationProcessWorkflowProps {
  /** 상단 눈썹 라벨 (예: "운영 템플릿"). */
  eyebrow: string;
  /** 페이지 제목 (예: "설치·구축 업무 절차"). */
  title: string;
  /** 제목 아래 보조 설명 (선택). */
  subtitle?: string;
  fetcher: () => Promise<OperationProcessData>;
  saver: (data: OperationProcessData) => Promise<void>;
  /** 단계별 첨부 storage projectId. 없으면 첨부 버튼 숨김. */
  attachmentsProjectId?: string;
  /** true 면 조회 전용 — 편집 컨트롤 숨김 + 모든 변경/저장 차단. */
  readOnly?: boolean;
}

export default function OperationProcessWorkflow(props: OperationProcessWorkflowProps) {
  const { fetcher, saver, attachmentsProjectId, readOnly } = props;
  // 앱 다크/라이트 토글을 따르는 디자인 토큰.
  const wf = useWorkflowTokens();
  const isDark = useTheme().palette.mode === 'dark';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<OperationProcessData | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  // uncontrolled 입력(defaultValue)을 서버 재로드 값으로 갱신하기 위한 remount key.
  const [reloadKey, setReloadKey] = useState(0);

  const dataRef = useRef<OperationProcessData | null>(null);
  const pendingRef = useRef<OperationProcessData | null>(null);
  const savingRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const d = await fetcher();
      dataRef.current = d;
      setData(d);
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setLoadError(err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  // 최초 1회만 로드 — fetcher 가 인라인 함수로 와도 재로드 루프에 빠지지 않게
  // (OperationProcessTable 과 동일한 mount-only 패턴).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gothic A1 웹폰트 — JSX <link precedence> 는 React 19 가 스타일시트 load
  // 이벤트를 기다리며 콘텐츠 커밋을 지연시킨다(폰트 호스트 불통/jsdom 에서
  // 화면이 안 뜸). 부작용으로 1회만 주입하고, font-display:swap 이라 폰트
  // 로드 전에도 폴백 서체로 즉시 렌더된다.
  useEffect(() => {
    const ID = 'op-workflow-gothic-a1';
    if (document.getElementById(ID)) return;
    const link = document.createElement('link');
    link.id = ID;
    link.rel = 'stylesheet';
    link.href = GOTHIC_A1_CSS;
    document.head.appendChild(link);
  }, []);

  /**
   * 저장 큐 — 연속 변경은 마지막 스냅샷만 저장(coalesce), 순서 보장.
   * 실패 시 대기분 폐기 + 알림 + 서버 상태로 재로드.
   */
  const flush = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      while (pendingRef.current) {
        const payload = pendingRef.current;
        pendingRef.current = null;
        await saver(payload);
      }
    } catch (err: any) {
      pendingRef.current = null;
      window.alert(`저장 실패: ${err?.message ?? err}`);
      await load();
    } finally {
      savingRef.current = false;
      if (pendingRef.current) void flush();
    }
  }, [saver, load]);

  const mutate = useCallback(
    (fn: (rows: OperationProcessRow[]) => OperationProcessRow[]) => {
      if (readOnly) return;
      const cur = dataRef.current;
      if (!cur) return;
      const next: OperationProcessData = { ...cur, rows: fn(cur.rows) };
      dataRef.current = next;
      setData(next);
      pendingRef.current = next;
      void flush();
    },
    [flush, readOnly],
  );

  // ── 단계별 첨부 개수 (배지) ──
  const [attachmentCounts, setAttachmentCounts] = useState<Record<string, number>>({});
  const refreshAttachmentCounts = useCallback(async () => {
    if (!attachmentsProjectId) return;
    try {
      setAttachmentCounts(await OperationProcessFileService.countsByStep(attachmentsProjectId));
    } catch (err) {
      console.error('첨부 카운트 로드 실패:', err);
    }
  }, [attachmentsProjectId]);
  useEffect(() => {
    refreshAttachmentCounts();
  }, [refreshAttachmentCounts]);

  const view = useMemo(() => (data ? buildWorkflowView(data.rows) : null), [data]);

  const actions: WorkflowActions = useMemo(
    () => ({
      toggleExpand: (rowId) =>
        setExpanded((prev) => {
          const rows = dataRef.current?.rows ?? [];
          const v = buildWorkflowView(rows);
          const st = v.steps.find((s) => s.row.id === rowId);
          const cur = prev[rowId] ?? st?.status === 'current';
          return { ...prev, [rowId]: !cur };
        }),
      startEdit: (id) => setEditingId(id),
      commitTitle: (ref, value) => {
        setEditingId(null);
        const v = value.trim();
        if (!v) return;
        if (ref.childId) mutate((rows) => patchChild(rows, ref.rowId, ref.childId!, { name: v }));
        else mutate((rows) => patchRow(rows, ref.rowId, { name: v }));
      },
      patchRow: (rowId, patch) => mutate((rows) => patchRow(rows, rowId, patch)),
      patchChild: (rowId, childId, patch) =>
        mutate((rows) => patchChild(rows, rowId, childId, patch)),
      setDone: (ref, done) => mutate((rows) => setUnitDone(rows, ref, done)),
      toggleCheckItem: (ref, itemId) => mutate((rows) => toggleChecklistItem(rows, ref, itemId)),
      addCheckItem: (ref, text) => mutate((rows) => addChecklistItem(rows, ref, text)),
      removeCheckItem: (ref, itemId) => mutate((rows) => removeChecklistItem(rows, ref, itemId)),
      addSub: (rowId) => {
        let newId = '';
        mutate((rows) => {
          const res = addSubStep(rows, rowId);
          newId = res.id;
          return res.rows;
        });
        setExpanded((prev) => ({ ...prev, [rowId]: true }));
        if (newId) setEditingId(newId);
      },
      removeStep: (rowId) => mutate((rows) => removeStep(rows, rowId)),
      removeSub: (rowId, childId) => mutate((rows) => removeSubStep(rows, rowId, childId)),
      moveStep: (rowId, dir) => mutate((rows) => moveStep(rows, rowId, dir)),
      moveSub: (rowId, childId, dir) => mutate((rows) => moveSubStep(rows, rowId, childId, dir)),
    }),
    [mutate],
  );

  const handleAddStep = useCallback(() => {
    let newId = '';
    mutate((rows) => {
      const res = addStep(rows);
      newId = res.id;
      return res.rows;
    });
    if (newId) {
      setExpanded((prev) => ({ ...prev, [newId]: true }));
      setEditingId(newId);
    }
  }, [mutate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10, bgcolor: wf.bg }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError || !view) {
    return (
      <Box sx={{ py: 10, textAlign: 'center', fontFamily: wf.font, color: wf.muted }}>
        불러오기 실패: {loadError}
        <Box
          component="button"
          type="button"
          onClick={load}
          sx={{
            display: 'block',
            m: '16px auto 0',
            fontFamily: wf.font,
            fontSize: 13,
            fontWeight: 700,
            p: '8px 16px',
            borderRadius: '7px',
            border: `1px solid ${wf.lineSoft}`,
            bgcolor: wf.paper,
            cursor: 'pointer',
          }}
        >
          다시 시도
        </Box>
      </Box>
    );
  }

  const { steps, percent, doneCount, totalCount, current } = view;

  return (
    <Box
      sx={{
        bgcolor: wf.bg,
        color: wf.ink,
        fontFamily: wf.font,
        minHeight: '100%',
        // 네이티브 폼 컨트롤(체크박스/셀렉트)과 스크롤바가 모드를 따르게.
        colorScheme: isDark ? 'dark' : 'light',
      }}
    >
      <Box sx={{ maxWidth: 920, mx: 'auto', p: '48px 32px 96px' }}>
        {/* ── 헤더 ── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: '24px',
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Box sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', color: wf.acc }}>
              {props.eyebrow}
            </Box>
            <Box
              component="h1"
              sx={{ m: '8px 0 0', fontSize: 34, fontWeight: 900, letterSpacing: '-0.02em' }}
            >
              {props.title}
            </Box>
            {props.subtitle && (
              <Box sx={{ mt: '8px', fontSize: 12, color: wf.muted }}>{props.subtitle}</Box>
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ fontSize: 40, fontWeight: 900, lineHeight: 1, color: wf.accDeep }}>
              {percent}%
            </Box>
            <Box sx={{ fontSize: 12, color: wf.muted }}>
              {doneCount} / {totalCount} 단계 완료
            </Box>
          </Box>
        </Box>

        {/* 진행 게이지 */}
        <Box sx={{ mt: '20px', height: 8, borderRadius: '4px', bgcolor: wf.track, overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              borderRadius: '4px',
              bgcolor: wf.acc,
              transition: 'width 0.3s',
              width: `${percent}%`,
            }}
          />
        </Box>

        {/* 현재 / 다음 단계 카드 */}
        <Box
          sx={{
            mt: '24px',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: '12px',
          }}
        >
          <Box sx={{ bgcolor: wf.ink, color: wf.bg, borderRadius: '10px', p: '16px 20px' }}>
            <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: wf.accBright }}>
              현재 단계
            </Box>
            <Box sx={{ fontSize: 17, fontWeight: 700, mt: '6px' }}>{view.currentLabel}</Box>
          </Box>
          <Box sx={{ bgcolor: wf.paper, border: `1px solid ${wf.line}`, borderRadius: '10px', p: '16px 20px' }}>
            <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: wf.muted }}>
              다음 단계
            </Box>
            <Box sx={{ fontSize: 17, fontWeight: 700, mt: '6px' }}>{view.nextLabel}</Box>
            {view.nextPrep && (
              <Box sx={{ fontSize: 12, color: wf.accDeep, fontWeight: 500, mt: '4px' }}>
                {view.nextPrep}
              </Box>
            )}
          </Box>
        </Box>

        {/* ── 단계 타임라인 ── */}
        {/* readOnly: 버튼(<button>)·편집 글리프/추가 입력(.wf-editctl)을 숨기고
            입력류는 클릭만 막아 값 표시는 유지한다 (펼침 헤더는 div 라 영향 없음). */}
        <Box
          sx={{
            mt: '40px',
            ...(readOnly && {
              '& button': { display: 'none' },
              '& .wf-editctl': { display: 'none' },
              '& input, & textarea': { pointerEvents: 'none' },
            }),
          }}
        >
          {steps.map((step) => (
            <WorkflowStepCard
              key={`${step.row.id}:${reloadKey}`}
              step={step}
              stepCount={steps.length}
              currentChildId={
                current && current.rowId === step.row.id ? current.childId ?? null : null
              }
              expanded={expanded[step.row.id] ?? step.status === 'current'}
              editingId={editingId}
              attachmentsProjectId={attachmentsProjectId}
              attachmentCounts={attachmentCounts}
              onAttachmentsChanged={refreshAttachmentCounts}
              actions={actions}
            />
          ))}

          {/* 단계 추가 */}
          {!readOnly && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '52px 1fr' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: `2px dashed ${wf.dash}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: wf.faint,
                  fontWeight: 700,
                }}
              >
                +
              </Box>
            </Box>
            <Box sx={{ pl: '12px' }}>
              <Box
                component="button"
                type="button"
                onClick={handleAddStep}
                sx={{
                  fontFamily: wf.font,
                  fontSize: 14,
                  fontWeight: 700,
                  p: '9px 18px',
                  borderRadius: '9px',
                  border: `2px dashed ${wf.dash}`,
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  color: wf.muted,
                  '&:hover': { borderColor: wf.acc, color: wf.accDeep },
                }}
              >
                단계 추가
              </Box>
            </Box>
          </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
